import { supabase } from "../lib/supabase";
import { authService } from "./authService";
import JSZip from "jszip";
import {
  collectStorageFolderFiles,
  parseManagedStorageReference,
} from "../utils/courseAssetLifecycle";
import {
  LEARNER_UPLOADS_BUCKET,
  isOpenableLegacyUrl,
  parseLearnerUploadRef,
  toLearnerUploadRef,
} from "../utils/learnerUploads";

export const MAX_LESSON_DOCUMENT_BYTES = 50 * 1024 * 1024;

export const uploadService = {
  // Upload course thumbnail
  async uploadCourseThumbnail(file, onProgress = null) {
    try {
      const {
        data: { user },
      } = await supabase?.auth?.getUser();
      if (!user) throw new Error("User not authenticated");

      const fileExt = file?.name?.split(".")?.pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 9)}.${fileExt}`;
      const filePath = `${user.id}/thumbnails/${fileName}`;

      const { data, error } = await supabase?.storage
        ?.from("course-content")
        ?.upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) throw error;

      // Get public URL
      const { data: publicUrlData } = supabase?.storage
        ?.from("course-content")
        ?.getPublicUrl(filePath);

      return publicUrlData?.publicUrl;
    } catch (error) {
      console.error("Thumbnail upload error:", error);
      throw new Error("Failed to upload thumbnail");
    }
  },

  // Upload an image or video used on an AI Studio slide. Returns its public URL.
  async uploadSlideMedia(file, courseId = null) {
    const {
      data: { user },
    } = await supabase?.auth?.getUser();
    if (!user) throw new Error("User not authenticated");

    const ext = file?.name?.split(".")?.pop()?.toLowerCase() || "bin";
    const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 9)}.${ext}`;
    const filePath = `${user.id}/ai-course-media/${courseId || "general"}/${fileName}`;

    const { error } = await supabase?.storage
      ?.from("course-content")
      ?.upload(filePath, file, { cacheControl: "3600", upsert: false });
    if (error) throw error;

    const { data: publicUrlData } = supabase?.storage
      ?.from("course-content")
      ?.getPublicUrl(filePath);
    return publicUrlData?.publicUrl;
  },

  // Upload course video to course-videos bucket
  async uploadCourseVideo(
    file,
    courseId = null,
    lessonId = null,
    onProgress = null,
  ) {
    try {
      const {
        data: { user },
      } = await supabase?.auth?.getUser();
      if (!user) throw new Error("User not authenticated");

      // Validate file type
      const allowedTypes = [
        "video/mp4",
        "video/webm",
        "video/ogg",
        "video/quicktime",
        "video/x-msvideo",
      ];
      if (!allowedTypes.includes(file.type)) {
        throw new Error(
          "Invalid file type. Please upload MP4, WebM, OGG, MOV (QuickTime), or AVI files.",
        );
      }

      // Check file size (500GB limit for Supabase Pro plan)
      const maxSize = 500 * 1024 * 1024 * 1024; // 500GB
      if (file.size > maxSize) {
        throw new Error("File size too large. Maximum size is 500GB.");
      }

      const fileExt = file?.name?.split(".")?.pop();
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 15);
      const fileName = `${user?.id}_${timestamp}_${randomId}.${fileExt}`;
      const filePath = `${user.id}/videos/${fileName}`;

      onProgress?.(10);

      const { data, error } = await supabase?.storage
        ?.from("course-content")
        ?.upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
          contentType: this.getContentType(fileName),
          metadata: {
            owner: user?.id,
            courseId: courseId || "unknown",
            lessonId: lessonId || "unknown",
            uploadedAt: new Date().toISOString(),
          },
        });

      if (error) throw error;

      onProgress?.(80);

      // Get public URL since course-content bucket is public
      const { data: publicUrlData } = supabase?.storage
        ?.from("course-content")
        ?.getPublicUrl(filePath);

      onProgress?.(100);

      return publicUrlData?.publicUrl;
    } catch (error) {
      console.error("Video upload error:", error);
      throw error;
    }
  },

  // Upload SCORM package via server-side Edge Function (preferred)
  async uploadScormPackage(file, courseId, lessonId, onProgress = null) {
    const session = await authService.getValidSession();
    if (!session?.access_token || !session.user?.id) {
      throw new Error("Authentication required");
    }
    const userId = session.user.id;

    try {
      // Get Supabase URL from the supabase client
      const supabaseUrl = supabase.supabaseUrl;
      const functionEndpoint = `${supabaseUrl}/functions/v1/process-scorm`;

      // Prefer server-side upload: POST multipart/form-data to Edge Function
      const formData = new FormData();
      formData.append("file", file);
      if (courseId) formData.append("courseId", courseId);
      if (lessonId) formData.append("lessonId", lessonId);
      onProgress?.(5);

      const authHeader = {
        Authorization: `Bearer ${session.access_token}`,
      };

      onProgress?.(15); // File uploaded to Edge Function

      // Start the fetch request
      const fetchPromise = fetch(functionEndpoint, {
        method: "POST",
        body: formData,
        headers: authHeader, // allow browser to set multipart boundary; only add auth header
      });

      onProgress?.(25); // Edge Function received file

      // Simulate processing progress while waiting for response
      let currentProgress = 25;
      const progressInterval = setInterval(() => {
        if (currentProgress < 85) {
          currentProgress += 5;
          onProgress?.(currentProgress);
        }
      }, 300); // Update every 300ms

      let resp;
      try {
        resp = await fetchPromise;
        clearInterval(progressInterval);
        onProgress?.(90); // Processing complete
      } catch (e) {
        clearInterval(progressInterval);
        throw e;
      }

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        console.warn("Server-side processing failed:", errorData);

        // Handle specific error codes
        if (errorData.code === "FILE_TOO_LARGE") {
          throw new Error(`File too large: ${errorData.error}`);
        }

        if (errorData.code === "INVALID_SCORM") {
          throw new Error(`Invalid SCORM package: ${errorData.error}`);
        }

        // For other errors, fall back to client-side processing
        throw new Error(`Server SCORM processing failed: ${resp.status}`);
      }

      const json = await resp.json();
      onProgress?.(100);

      // Response shape: { extractionPath, launchUrl, manifest, uploadedFiles }
      return {
        launchUrl: json.launchUrl || null,
        manifest: json.manifest || null,
        extractedFiles: Array.isArray(json.uploadedFiles)
          ? json.uploadedFiles.length
          : 0,
        extractionPath: json.extractionPath || null,
      };
    } catch (error) {
      console.warn(
        "Server-side SCORM upload failed, falling back to client-side processing:",
        error,
      );

      // Fallback: do the original client-side extraction if server call fails
      // Reuse existing client-side extraction logic by creating a Blob -> JSZip flow
      try {
        // Import JSZip dynamically with proper error handling
        let JSZip;
        try {
          JSZip = (await import("jszip")).default;
        } catch (importError) {
          console.error("Failed to import JSZip:", importError);
          throw new Error(
            "JSZip library is not available. Please ensure the development server is running or JSZip is properly bundled.",
          );
        }
        const zip = new JSZip();
        onProgress?.(10);
        const zipContents = await zip.loadAsync(file);
        onProgress?.(30);

        // Find manifest - check root first, then search subdirectories
        let manifestFile = zipContents.file("imsmanifest.xml");
        let scormRootPrefix = "";

        if (!manifestFile) {
          const allFiles = Object.keys(zipContents.files);
          const manifestPath = allFiles.find((p) =>
            p.toLowerCase().endsWith("imsmanifest.xml"),
          );
          if (manifestPath) {
            manifestFile = zipContents.file(manifestPath);
            const lastSlash = manifestPath.lastIndexOf("/");
            if (lastSlash !== -1) {
              scormRootPrefix = manifestPath.substring(0, lastSlash + 1);
              console.log(
                `Found manifest in subfolder, prefix: "${scormRootPrefix}"`,
              );
            }
          }
        }

        if (!manifestFile) {
          throw new Error("imsmanifest.xml not found in SCORM package");
        }
        const manifestXml = await manifestFile.async("text");
        const manifest = this.parseScormManifestSimple(manifestXml);
        onProgress?.(50);

        // Create extraction path using courseId (lessonId may not exist yet for new lessons)
        let extractionPath;

        if (courseId) {
          const subId = lessonId || `new-${Date.now()}`;
          extractionPath = `${userId}/scorm-extracted/${courseId}/${subId}/${Date.now()}`;
        } else {
          extractionPath = `${userId}/scorm-extracted/temp/${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          console.warn("No courseId provided - using temp path");
        }

        const extractedFiles = [];

        // Get all files to upload - only include files under the SCORM root prefix
        const filesToUpload = Object.entries(zipContents.files).filter(
          ([path, zipEntry]) =>
            !zipEntry.dir &&
            (!scormRootPrefix || path.startsWith(scormRootPrefix)),
        );
        const totalFiles = filesToUpload.length;
        let uploadedCount = 0;
        const uploadedPaths = [];

        try {
          // Upload files sequentially with progress updates
          for (const [relativePath, zipEntry] of filesToUpload) {
            const fileContent = await zipEntry.async("blob");
            // Strip subfolder prefix so files are stored at correct relative paths
            const strippedPath = scormRootPrefix
              ? relativePath.substring(scormRootPrefix.length)
              : relativePath;
            const filePath = `${extractionPath}/${strippedPath}`;
            const contentType = this.getContentType(strippedPath);

            uploadedPaths.push(filePath);

            // Create a new blob with the correct MIME type to ensure proper content type
            const typedBlob = new Blob([fileContent], { type: contentType });

            const result = await supabase?.storage
              ?.from("course-content")
              ?.upload(filePath, typedBlob, {
                contentType,
                cacheControl: "3600",
                upsert: true,
              });

            if (result.error) {
              throw result.error;
            }

            extractedFiles.push(strippedPath);
            uploadedCount++;

            // Update progress incrementally (50% to 90% range for file uploads)
            const uploadProgress =
              50 + Math.round((uploadedCount / totalFiles) * 40);
            onProgress?.(uploadProgress);
          }

          onProgress?.(90);

          const launchFilePath = `${extractionPath}/${manifest.launchFile}`;
          const { data: launchUrlData } = supabase?.storage
            ?.from("course-content")
            ?.getPublicUrl(launchFilePath);

          if (!launchUrlData?.publicUrl) {
            throw new Error("Failed to generate launch URL");
          }

          onProgress?.(100);
          return {
            launchUrl: launchUrlData.publicUrl,
            manifest: manifest,
            extractedFiles: extractedFiles.length,
            extractionPath: extractionPath,
          };
        } catch (uploadError) {
          console.error(
            "Client-side SCORM upload failed, cleaning up:",
            uploadError,
          );

          // Clean up partially uploaded files on failure
          if (uploadedPaths.length > 0) {
            try {
              // Delete in batches of 100
              for (let i = 0; i < uploadedPaths.length; i += 100) {
                await supabase?.storage
                  ?.from("course-content")
                  ?.remove(uploadedPaths.slice(i, i + 100));
              }
            } catch (cleanupError) {
              console.error("Failed to cleanup partial upload:", cleanupError);
            }
          }

          throw uploadError;
        }
      } catch (fallbackError) {
        console.error("Client-side SCORM fallback failed:", fallbackError);
        throw new Error(
          fallbackError.message ||
            "SCORM processing failed both server-side and client-side",
        );
      }
    }
  },

  // Upload lesson document (PDF, DOC, etc.)
  async uploadLessonDocument(
    file,
    courseId = null,
    lessonId = null,
    onProgress = null,
  ) {
    try {
      const {
        data: { user },
      } = await supabase?.auth?.getUser();
      if (!user) throw new Error("User not authenticated");

      if (file?.size > MAX_LESSON_DOCUMENT_BYTES) {
        throw new Error("Document must be 50MB or smaller");
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      const allowedExtensions = [
        ".pdf",
        ".doc",
        ".docx",
        ".ppt",
        ".pptx",
        ".xls",
        ".xlsx",
      ];

      const fileExt = "." + file?.name?.split(".")?.pop()?.toLowerCase();
      if (
        !allowedTypes.includes(file?.type) &&
        !allowedExtensions.includes(fileExt)
      ) {
        throw new Error(
          "Please upload a PDF, DOC, DOCX, PPT, PPTX, XLS, or XLSX file",
        );
      }

      const sanitizedName = file?.name?.replace(/[^a-zA-Z0-9.-]/g, "_");
      const fileName = `${user?.id}/documents/${courseId || "general"}/${lessonId || "lesson"}/${Date.now()}_${sanitizedName}`;
      const filePath = fileName;

      const { data, error } = await supabase?.storage
        ?.from("course-resources")
        ?.upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
          metadata: {
            owner: user?.id,
            courseId: courseId,
            lessonId: lessonId,
            originalName: file?.name,
            fileSize: file?.size?.toString(),
          },
        });

      if (error) throw error;

      // Use public URL since course-resources bucket is public
      const { data: publicUrlData } = supabase?.storage
        ?.from("course-resources")
        ?.getPublicUrl(filePath);

      return {
        filePath,
        fileUrl: publicUrlData?.publicUrl,
        fileName: file?.name,
        fileSize: file?.size,
        fileType: file?.type,
      };
    } catch (error) {
      console.error("Document upload error:", error);
      throw new Error(error?.message || "Failed to upload document");
    }
  },

  // Upload course resource/attachment
  async uploadCourseResource(file, courseId = null, onProgress = null) {
    try {
      const {
        data: { user },
      } = await supabase?.auth?.getUser();
      if (!user) throw new Error("User not authenticated");

      const fileExt = file?.name?.split(".")?.pop();
      const sanitizedName = file?.name?.replace(/[^a-zA-Z0-9.-]/g, "_");
      const fileName = `${user?.id}/resources/${courseId || "general"}/${Date.now()}_${sanitizedName}`;
      const filePath = fileName;

      const { data, error } = await supabase?.storage
        ?.from("course-resources")
        ?.upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
          metadata: {
            owner: user?.id,
            courseId: courseId,
            originalName: file?.name,
            fileSize: file?.size?.toString(),
          },
        });

      if (error) throw error;

      // Get signed URL for download
      const { data: signedUrlData, error: urlError } = await supabase?.storage
        ?.from("course-resources")
        ?.createSignedUrl(filePath, 3600 * 24 * 7); // 7 days expiry

      if (urlError) throw urlError;

      return {
        filePath,
        fileUrl: signedUrlData?.signedUrl,
        fileName: file?.name,
        fileSize: file?.size,
        fileType: file?.type,
      };
    } catch (error) {
      console.error("Resource upload error:", error);
      throw new Error("Failed to upload course resource");
    }
  },

  // Delete file from storage
  async deleteFile(bucket, filePath) {
    try {
      const { error } = await supabase?.storage
        ?.from(bucket)
        ?.remove([filePath]);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error("File deletion error:", error);
      throw new Error("Failed to delete file");
    }
  },

  // Get file metadata
  async getFileInfo(bucket, filePath) {
    try {
      const { data, error } = await supabase?.storage?.from(bucket)?.list("", {
        limit: 1,
        search: filePath,
      });

      if (error) throw error;
      return data?.[0] || null;
    } catch (error) {
      console.error("File info error:", error);
      throw new Error("Failed to get file information");
    }
  },

  // Generate signed URL for private files
  async getSignedUrl(bucket, filePath, expiresIn = 3600) {
    try {
      const { data, error } = await supabase?.storage
        ?.from(bucket)
        ?.createSignedUrl(filePath, expiresIn);

      if (error) throw error;
      return data?.signedUrl;
    } catch (error) {
      console.error("Signed URL error:", error);
      throw new Error("Failed to generate file URL");
    }
  },

  // Get public URL for public files
  getPublicUrl(bucket, filePath) {
    const { data } = supabase?.storage?.from(bucket)?.getPublicUrl(filePath);

    return data?.publicUrl;
  },

  // Simple browser-compatible SCORM manifest parser (no xml2js dependency)
  parseScormManifestSimple(manifestXml) {
    try {
      // Use native browser DOMParser instead of xml2js
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(manifestXml, "text/xml");

      // Check for parsing errors
      const parseError = xmlDoc.querySelector("parsererror");
      if (parseError) {
        throw new Error("Invalid XML format");
      }

      const manifest = xmlDoc.querySelector("manifest");
      if (!manifest) {
        throw new Error("No manifest element found");
      }

      // Extract basic info
      const title =
        xmlDoc.querySelector("organization > title")?.textContent ||
        "SCORM Package";
      const identifier = manifest.getAttribute("identifier") || "scorm-package";
      const version = manifest.getAttribute("version") || "1.2";

      // Find launch file from resources
      let launchFile = "";
      const resources = xmlDoc.querySelectorAll("resource");
      if (resources.length > 0) {
        // Look for main SCO resource
        const mainResource =
          Array.from(resources).find(
            (r) =>
              r.getAttribute("type") === "webcontent" ||
              r.getAttribute("adlcp:scormtype") === "sco",
          ) || resources[0];

        launchFile = mainResource?.getAttribute("href") || "";
      }

      // Extract all resources
      const resourceList = Array.from(resources).map((r) => ({
        identifier: r.getAttribute("identifier") || "",
        href: r.getAttribute("href") || "",
        type: r.getAttribute("type") || "webcontent",
      }));

      return {
        title,
        identifier,
        version,
        launchFile,
        resources: resourceList,
      };
    } catch (error) {
      throw new Error(`Failed to parse manifest XML: ${error.message}`);
    }
  },

  // Helper method to determine content type
  getContentType(filename) {
    const ext = filename.toLowerCase().split(".").pop();
    const contentTypes = {
      html: "text/html; charset=utf-8",
      htm: "text/html; charset=utf-8",
      css: "text/css; charset=utf-8",
      js: "application/javascript; charset=utf-8",
      json: "application/json; charset=utf-8",
      xml: "application/xml; charset=utf-8",
      xsd: "application/xml; charset=utf-8",
      png: "image/png",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      gif: "image/gif",
      svg: "image/svg+xml",
      mp4: "video/mp4",
      mp3: "audio/mpeg",
      ttf: "font/ttf",
      woff: "font/woff",
      woff2: "font/woff2",
      otf: "font/otf",
      pdf: "application/pdf",
      swf: "application/x-shockwave-flash",
      ico: "image/x-icon",
    };
    return contentTypes[ext] || "application/octet-stream";
  },

  // Delete all files under a storage folder path (for SCORM cleanup on re-upload)
  async deleteStorageFolder(bucket, folderPath) {
    if (!folderPath || folderPath.length < 10) return; // safety check
    const storageBucket = supabase.storage.from(bucket);
    const filePaths = await collectStorageFolderFiles(
      folderPath.replace(/\/+$/, ""),
      async (path, options) => {
        const { data, error } = await storageBucket.list(path, {
          ...options,
          sortBy: { column: "name", order: "asc" },
        });
        if (error) throw error;
        return data || [];
      },
    );
    for (let i = 0; i < filePaths.length; i += 100) {
      const { error } = await storageBucket.remove(filePaths.slice(i, i + 100));
      if (error) throw error;
    }
  },

  async deleteStorageReference(value) {
    const reference =
      typeof value === "string" ? parseManagedStorageReference(value) : value;
    if (!reference?.bucket || !reference?.path) return;
    if (reference.folder) {
      await this.deleteStorageFolder(reference.bucket, reference.path);
      return;
    }
    const { error } = await supabase.storage
      .from(reference.bucket)
      .remove([reference.path]);
    if (error) throw error;
  },

  async deleteStorageReferences(values = []) {
    const results = await Promise.allSettled(
      values.map((value) => this.deleteStorageReference(value)),
    );
    const failures = results.filter((result) => result.status === "rejected");
    if (failures.length > 0) {
      throw new Error(`Could not remove ${failures.length} storage item(s)`);
    }
  },

  // Batch upload files
  async uploadMultipleFiles(
    files,
    bucket,
    folder = "",
    courseId = null,
    lessonId = null,
  ) {
    const results = [];
    const errors = [];

    const uploadFiles = Array.isArray(files) ? files : [];
    for (const file of uploadFiles) {
      try {
        let result;
        switch (bucket) {
          case "course-thumbnails":
            result = await this.uploadCourseThumbnail(file);
            break;
          case "course-content":
            result = await this.uploadCourseVideo(file);
            break;
          case "course-resources":
            result = await this.uploadCourseResource(file, courseId);
            break;
          case "lesson-documents":
            result = await this.uploadLessonDocument(file, courseId, lessonId);
            break;
          default:
            throw new Error(`Unsupported bucket: ${bucket}`);
        }
        results?.push({ file, result });
      } catch (error) {
        errors?.push({ file, error: error?.message });
      }
    }

    return { results, errors };
  },

  // General upload file method
  async uploadFile(file, bucket, folder = "", onProgress = null) {
    try {
      switch (bucket) {
        case "course-thumbnails":
          return await this.uploadCourseThumbnail(file, onProgress);
        case "course-content":
          // Check if it's an image file for thumbnail upload
          if (file.type && file.type.startsWith("image/")) {
            return await this.uploadCourseThumbnail(file, onProgress);
          } else {
            return await this.uploadCourseVideo(file, null, null, onProgress);
          }
        case "course-resources":
          return await this.uploadCourseResource(file, null, onProgress);
        case "lesson-documents":
          return await this.uploadLessonDocument(file, null, null, onProgress);
        default:
          throw new Error(`Unsupported bucket: ${bucket}`);
      }
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  },

  /**
   * Upload partner logo to partner-logos bucket.
   * Used by partner onboarding and branding settings.
   * @param {File} file - The logo file to upload
   * @param {string} partnerId - Partner slug or ID for unique filename
   * @returns {string} Public URL of the uploaded logo
   */
  async uploadPartnerLogo(file, partnerId) {
    try {
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        throw new Error("Logo must be less than 2MB");
      }

      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/svg+xml",
        "image/avif",
      ];
      if (!allowedTypes.includes(file.type)) {
        throw new Error("Please upload PNG, JPG, GIF, WebP, SVG, or AVIF.");
      }

      const session = await authService.getValidSession();
      if (!session?.access_token) {
        throw new Error("Session expired. Please sign in again.");
      }

      // Generate unique filename
      const fileExt = file.name.split(".").pop();
      const fileName = `${partnerId || Date.now()}-logo-${Date.now()}.${fileExt}`;
      const filePath = `${partnerId}/logos/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from("partner-logos")
        .upload(filePath, file, { upsert: true, cacheControl: "3600" });

      if (error) {
        console.error("Logo upload error:", error);
        throw new Error("Failed to upload logo");
      }

      // Get public URL
      const {
        data: { publicUrl },
      } = supabase.storage.from("partner-logos").getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error("Partner logo upload error:", error);
      throw error;
    }
  },

  /**
   * Upload a learner's prior certificate for a refresher course to the
   * private learner-uploads bucket.
   * @returns {string} `learner-uploads/<path>` reference
   */
  async uploadRefresherCertificate(file, courseId) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated");
    if (file.size > 10 * 1024 * 1024) {
      throw new Error("File must be less than 10MB");
    }
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
    const fileName = `${user.id}/refresher/${courseId || "general"}/${Date.now()}_${sanitizedName}`;
    const { error } = await supabase.storage
      .from(LEARNER_UPLOADS_BUCKET)
      .upload(fileName, file, { cacheControl: "3600", upsert: false });
    if (error) throw error;
    return toLearnerUploadRef(fileName);
  },

  /**
   * Short-lived URL for a learner upload reference. Legacy http(s) URLs are
   * returned unchanged; anything else resolves to null.
   */
  async getLearnerUploadUrl(reference, expiresInSeconds = 600) {
    const objectPath = parseLearnerUploadRef(reference);
    if (!objectPath) return isOpenableLegacyUrl(reference) ? reference : null;
    const { data, error } = await supabase.storage
      .from(LEARNER_UPLOADS_BUCKET)
      .createSignedUrl(objectPath, expiresInSeconds);
    if (error) throw error;
    return data?.signedUrl || null;
  },

  /**
   * Upload access request attachment (payment screenshot, reference doc, etc.)
   * Stored in the private learner-uploads bucket under access-requests/.
   * @param {File} file - The file to upload
   * @param {string} userId - User ID for path namespacing
   * @returns {{ fileUrl: string, fileName: string, fileSize: number }} Upload result
   */
  async uploadAccessRequestAttachment(file, userId) {
    try {
      // Max 10MB
      if (file.size > 10 * 1024 * 1024) {
        throw new Error("File must be less than 10MB");
      }

      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const fileExt = file.name.split(".").pop().toLowerCase();
      const allowedExts = [
        "jpg",
        "jpeg",
        "png",
        "gif",
        "webp",
        "pdf",
        "doc",
        "docx",
      ];

      if (!allowedTypes.includes(file.type) && !allowedExts.includes(fileExt)) {
        throw new Error(
          "Please upload an image (JPG, PNG, GIF, WebP) or document (PDF, DOC, DOCX)",
        );
      }

      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const fileName = `${userId}/access-requests/${Date.now()}_${sanitizedName}`;

      // Payment receipts are personal documents: private bucket, readable by
      // the learner, platform admins and the partner reviewing the request.
      const { error } = await supabase.storage
        .from(LEARNER_UPLOADS_BUCKET)
        .upload(fileName, file, { cacheControl: "3600", upsert: false });

      if (error) throw error;

      return {
        fileUrl: toLearnerUploadRef(fileName),
        fileName: file.name,
        fileSize: file.size,
      };
    } catch (error) {
      console.error("Access request attachment upload error:", error);
      throw new Error(error.message || "Failed to upload attachment");
    }
  },

  /**
   * Upload partner banner to partner-logos bucket.
   * Used by partner onboarding and branding settings.
   * @param {File} file - The banner file to upload
   * @param {string} partnerId - Partner slug or ID for unique filename
   * @returns {string} Public URL of the uploaded banner
   */
  async uploadPartnerBanner(file, partnerId) {
    try {
      if (file.size > 5 * 1024 * 1024) {
        throw new Error("Banner must be less than 5MB");
      }

      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/svg+xml",
        "image/avif",
      ];
      if (!allowedTypes.includes(file.type)) {
        throw new Error("Please upload PNG, JPG, GIF, WebP, SVG, or AVIF.");
      }

      const session = await authService.getValidSession();
      if (!session?.access_token) {
        throw new Error("Session expired. Please sign in again.");
      }

      const fileExt = file.name.split(".").pop();
      const fileName = `${partnerId || Date.now()}-banner-${Date.now()}.${fileExt}`;
      const filePath = `${partnerId}/banners/${fileName}`;

      const { data, error } = await supabase.storage
        .from("partner-logos")
        .upload(filePath, file, { upsert: true });

      if (error) {
        console.error("Banner upload error:", error);
        throw new Error("Failed to upload banner");
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from("partner-logos").getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error("Partner banner upload error:", error);
      throw error;
    }
  },
};
